import dayjs from 'dayjs';

export const normalizeToDayjsDate = (value) => {
  if (!value) return null;
  const rawValue = String(value);
  const datePortion = rawValue.includes('T') ? rawValue.split('T')[0] : rawValue;
  const parsedDate = dayjs(datePortion).startOf('day');
  return parsedDate.isValid() ? parsedDate : null;
};

export const normalizeSublocationId = (value) => String(value ?? '').trim();

export const normalizeNumericSublocationId = (value) => {
  const normalizedId = normalizeSublocationId(value);
  if (!normalizedId || !/^\d+$/.test(normalizedId)) return '';
  return String(Number(normalizedId));
};

const getSublocationLabel = (row) => {
  const sublocationName = String(row?.name ?? '').trim();
  if (sublocationName) return sublocationName;
  const sublocationId = String(row?.id ?? '').trim();
  return sublocationId ? `Sublocation (${sublocationId})` : 'Selected sublocation';
};

export const buildGapConstraintWarning = (row, gapStartDate, gapEndDate, displayDateFormat) => {
  if (!row || !gapStartDate || !gapEndDate) return null;
  const sublocationId = String(row?.id ?? '').trim();
  const locationId = String(row?.locationId ?? '').trim();
  const sublocationName = getSublocationLabel(row);
  const gapStart = gapStartDate.format(displayDateFormat);
  const gapEnd = gapEndDate.format(displayDateFormat);
  const messageSuffix = ` has a gap between ${gapStart} to ${gapEnd}. Create a weekly posting for that date range first.`;

  return {
    key: `${sublocationId || sublocationName}-${gapStart}-${gapEnd}`,
    message: `${sublocationName}${messageSuffix}`,
    messageSuffix,
    sublocationName,
    sublocationId,
    locationId,
    gapStart,
    gapEnd,
  };
};

export const buildApiSublocationLookup = (apiSublocations = []) => {
  const byId = new Map();
  const byNumericId = new Map();

  apiSublocations.forEach((sublocation) => {
    const normalizedId = normalizeSublocationId(sublocation?.id);
    if (!normalizedId) return;

    byId.set(normalizedId, sublocation);

    const numericId = normalizeNumericSublocationId(normalizedId);
    if (numericId && !byNumericId.has(numericId)) {
      byNumericId.set(numericId, sublocation);
    }
  });

  return { byId, byNumericId };
};

export const resolveSelectedSublocationEndDateRows = (selectedSublocationIds = [], apiSublocationLookup = {}) => {
  if (selectedSublocationIds.length === 0) return [];

  const resolvedSublocations = [];
  const seenResolvedIds = new Set();

  selectedSublocationIds.forEach((selectedId) => {
    const directMatch = apiSublocationLookup.byId?.get(selectedId);
    const numericId = normalizeNumericSublocationId(selectedId);
    const numericMatch = !directMatch && numericId ? apiSublocationLookup.byNumericId?.get(numericId) : null;
    const matchedSublocation = directMatch || numericMatch;
    if (!matchedSublocation) return;

    const matchedSublocationId = normalizeSublocationId(matchedSublocation?.id);
    if (!matchedSublocationId || seenResolvedIds.has(matchedSublocationId)) return;
    seenResolvedIds.add(matchedSublocationId);

    resolvedSublocations.push({
      id: matchedSublocationId,
      locationId: String(matchedSublocation?.locationId ?? matchedSublocation?.location?.id ?? ''),
      name: matchedSublocation?.name ?? '',
      endDate: normalizeToDayjsDate(matchedSublocation?.lastPosting?.endDate),
    });
  });

  return resolvedSublocations;
};

export const deriveSelectionDateFacts = (selectedSublocationEndDateRows = []) => {
  const hasSelectedSublocations = selectedSublocationEndDateRows.length > 0;
  const isSingleSublocationSelected = selectedSublocationEndDateRows.length === 1;
  const singleSelectedEndDate = isSingleSublocationSelected ? selectedSublocationEndDateRows[0]?.endDate ?? null : null;

  const uniqueEndDateValues = new Set(
    selectedSublocationEndDateRows.filter((row) => row?.endDate).map((row) => row.endDate.format('YYYY-MM-DD')),
  );

  const hasAnyEndDate = selectedSublocationEndDateRows.some((row) => Boolean(row?.endDate));
  const hasAnyMissingEndDate = selectedSublocationEndDateRows.some((row) => !row?.endDate);

  const hasNoLastPostingForAll = hasSelectedSublocations && !hasAnyEndDate;
  const hasMixedKnownAndMissingEndDates = hasSelectedSublocations && hasAnyEndDate && hasAnyMissingEndDate;
  const hasConsistentKnownEndDate =
    hasSelectedSublocations && hasAnyEndDate && !hasAnyMissingEndDate && uniqueEndDateValues.size === 1;
  const hasMismatchedKnownEndDates =
    hasSelectedSublocations && hasAnyEndDate && !hasAnyMissingEndDate && uniqueEndDateValues.size > 1;

  const knownEndDateRows = selectedSublocationEndDateRows.filter((row) => Boolean(row?.endDate));
  const maxKnownEndDate =
    knownEndDateRows.length === 0
      ? null
      : knownEndDateRows.reduce(
          (latest, row) => (row.endDate.isAfter(latest, 'day') ? row.endDate : latest),
          knownEndDateRows[0].endDate,
        );
  const consistentKnownEndDate =
    !hasConsistentKnownEndDate || selectedSublocationEndDateRows.length === 0
      ? null
      : selectedSublocationEndDateRows.find((row) => row?.endDate)?.endDate ?? null;

  const effectiveReferenceEndDate = isSingleSublocationSelected
    ? singleSelectedEndDate
    : hasConsistentKnownEndDate
    ? consistentKnownEndDate
    : null;

  return {
    hasSelectedSublocations,
    hasMixedKnownAndMissingEndDates,
    hasMismatchedKnownEndDates,
    hasNoLastPostingForAll,
    knownEndDateRows,
    maxKnownEndDate,
    effectiveReferenceEndDate,
  };
};

export const deriveMinSelectableStartDate = ({
  effectiveReferenceEndDate,
  hasNoLastPostingForAll,
  startDateMin,
  noLastPostingMinDate,
}) => {
  let nextStartDate = null;

  if (effectiveReferenceEndDate) {
    nextStartDate = effectiveReferenceEndDate.add(1, 'day').startOf('day');
  } else if (hasNoLastPostingForAll) {
    nextStartDate = noLastPostingMinDate;
  }

  if (!nextStartDate) return null;

  if (nextStartDate.isBefore(startDateMin, 'day')) {
    return startDateMin;
  }

  return nextStartDate;
};

export const deriveDateConstraintWarnings = ({
  hasSelectedSublocations,
  minSelectableStartDate,
  maxKnownEndDate,
  knownEndDateRows,
  hasMixedKnownAndMissingEndDates,
  selectedSublocationEndDateRows,
  startDateMin,
  displayDateFormat,
}) => {
  if (!hasSelectedSublocations) return [];
  if (minSelectableStartDate) return [];
  if (!maxKnownEndDate) return [];

  const knownEndDateGapWarnings = knownEndDateRows
    .filter((row) => row?.endDate?.isBefore(maxKnownEndDate, 'day'))
    .map((row) => {
      const gapStartDate = row.endDate?.add(1, 'day').startOf('day');
      const gapEndDate = maxKnownEndDate.startOf('day');
      return buildGapConstraintWarning(row, gapStartDate, gapEndDate, displayDateFormat);
    })
    .filter(Boolean);

  const missingEndDateGapWarnings = hasMixedKnownAndMissingEndDates
    ? selectedSublocationEndDateRows
        .filter((row) => !row?.endDate)
        .map((row) => {
          const gapStartDate = startDateMin;
          const gapEndDate = maxKnownEndDate.startOf('day');
          return buildGapConstraintWarning(row, gapStartDate, gapEndDate, displayDateFormat);
        })
        .filter(Boolean)
    : [];

  return [...knownEndDateGapWarnings, ...missingEndDateGapWarnings];
};

export const deriveDateConstraintHint = ({
  hasSelectedSublocations,
  minSelectableStartDate,
  dateConstraintWarnings,
  hasMixedKnownAndMissingEndDates,
  hasMismatchedKnownEndDates,
  mismatchLabel,
}) => {
  if (!hasSelectedSublocations || minSelectableStartDate) return '';
  if (dateConstraintWarnings.length > 0) return dateConstraintWarnings[0].message;
  if (hasMixedKnownAndMissingEndDates || hasMismatchedKnownEndDates) return mismatchLabel;
  return 'Unable to determine a suggested date range from the selected sublocations.';
};

export const resolveDefaultDateUpdates = ({
  hasSelectedSublocations,
  minSelectableStartDate,
  selectedStartDate,
  selectedEndDate,
}) => {
  if (!hasSelectedSublocations || !minSelectableStartDate) return null;

  const currentStartDate = selectedStartDate ? dayjs(selectedStartDate).startOf('day') : null;
  const currentEndDate = selectedEndDate ? dayjs(selectedEndDate).startOf('day') : null;

  const needsDefaultStart = !currentStartDate?.isValid() || currentStartDate.isBefore(minSelectableStartDate, 'day');
  const startAnchorDate = needsDefaultStart ? minSelectableStartDate : currentStartDate;

  const defaultEndDate = startAnchorDate.startOf('day');
  const needsDefaultEndDate = !currentEndDate?.isValid() || currentEndDate.isBefore(defaultEndDate, 'day');

  if (!needsDefaultStart && !needsDefaultEndDate) return null;

  return {
    nextStartDate: needsDefaultStart ? minSelectableStartDate.format('YYYY-MM-DD') : null,
    nextEndDate: needsDefaultEndDate ? defaultEndDate.format('YYYY-MM-DD') : null,
  };
};
